"use client"

import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { CheckCircle, Heart, Camera, Sparkles, Instagram, ArrowRight } from "lucide-react"
import { motion } from "framer-motion"

interface ContactConfirmationProps {
  onClose: () => void
}

export function ContactConfirmation({ onClose }: ContactConfirmationProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", duration: 0.5 }}
      >
        <Card className="w-full max-w-md border-0 shadow-2xl bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50">
          <CardContent className="p-8 text-center space-y-6">
            {/* Success Icon with Animation */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", duration: 0.6 }}
              className="relative"
            >
              <div className="w-20 h-20 mx-auto bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>

              {/* Floating sparkles */}
              <motion.div
                animate={{
                  y: [-10, -20, -10],
                  rotate: [0, 180, 360],
                }}
                transition={{
                  duration: 2,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "easeInOut",
                }}
                className="absolute -top-2 -right-2"
              >
                <Sparkles className="w-6 h-6 text-purple-500" />
              </motion.div>

              <motion.div
                animate={{
                  y: [-5, -15, -5],
                  rotate: [0, -180, -360],
                }}
                transition={{
                  duration: 2.5,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "easeInOut",
                  delay: 0.5,
                }}
                className="absolute -bottom-1 -left-2"
              >
                <Heart className="w-5 h-5 text-pink-500 fill-current" />
              </motion.div>
            </motion.div>

            {/* Success Message */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="space-y-3"
            >
              <h3 className="text-2xl font-bold text-gray-900">Message Sent! 🎉</h3>
              <p className="text-gray-600 leading-relaxed">
                Thank you so much for reaching out! I'm excited to hear about your photography needs and will get back
                to you within 24 hours.
              </p>
            </motion.div>

            {/* Fun Stats */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="bg-white/60 rounded-lg p-4 space-y-2"
            >
              <div className="flex items-center justify-center space-x-2 text-purple-600">
                <Camera className="w-4 h-4" />
                <span className="text-sm font-medium">Average response time: 4 hours</span>
              </div>
              <div className="flex items-center justify-center space-x-2 text-pink-600">
                <Heart className="w-4 h-4 fill-current" />
                <span className="text-sm font-medium">99% client satisfaction rate</span>
              </div>
            </motion.div>

            {/* Call to Actions */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="space-y-3"
            >
              <p className="text-sm text-gray-600">While you wait, check out my latest work!</p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  asChild
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <a href="https://instagram.com/toribportrait_photography" target="_blank" rel="noopener noreferrer">
                    <Instagram className="w-4 h-4 mr-2" />
                    Follow on Instagram
                  </a>
                </Button>

                <Button
                  variant="outline"
                  className="flex-1 border-purple-300 text-purple-700 hover:bg-purple-50 bg-transparent"
                  onClick={onClose}
                >
                  Continue Browsing
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>

            {/* Fun Footer */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="text-xs text-gray-500 italic"
            >
              "Can't wait to capture your beautiful moments!" - Tori ✨
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  )
}
